import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

// Componentes básicos de la interfaz. Se mantienen mínimos y accesibles (labels, roles, foco visible).

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-marca/40 focus-visible:ring-offset-1';
const control =
  'block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 transition-colors hover:border-slate-400 focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/25 disabled:bg-slate-100 disabled:text-slate-500';
const labelClass = 'block text-sm font-medium text-slate-700';

// Por defecto no envía el formulario que la contiene: un botón de envío se declara con type="submit".
export function Button({
  variant = 'primary',
  className = '',
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' | 'link' | 'inverse' }) {
  const styles = {
    primary: 'bg-marca text-white hover:bg-marca-claro disabled:bg-slate-300 disabled:text-slate-600',
    secondary: 'border border-slate-300 bg-white text-slate-800 hover:border-slate-400 hover:bg-slate-50 disabled:text-slate-400',
    danger: 'bg-red-700 text-white hover:bg-red-800 disabled:bg-slate-300 disabled:text-slate-600',
    link: 'text-marca underline-offset-4 hover:underline disabled:text-slate-400',
    // Sobre fondos oscuros (la franja del ticket activo).
    inverse: 'border border-white/35 text-white hover:border-white/70 hover:bg-white/10 disabled:text-white/40',
  }[variant];
  const box = variant === 'link' ? 'rounded-sm' : 'rounded px-4 py-2';
  return <button type={type} className={`${box} text-sm font-medium transition-colors ${focusRing} disabled:cursor-not-allowed ${styles} ${className}`} {...props} />;
}

export function Field({ label, id, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; id: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input id={id} className={control} {...props} />
    </div>
  );
}

export function Textarea({ label, id, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; id: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <textarea id={id} rows={5} className={control} {...props} />
    </div>
  );
}

export function Select({
  label,
  id,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; id: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <select id={id} className={control} {...props}>
        {children}
      </select>
    </div>
  );
}

export function Checkbox({ label, hint, ...props }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode; hint?: string }) {
  return (
    <label className={`flex items-start gap-2 text-sm ${props.disabled ? 'text-slate-400' : 'text-slate-700'}`} title={hint}>
      <input type="checkbox" className="mt-0.5 h-4 w-4 rounded-sm border-slate-300 accent-marca" {...props} />
      <span>{label}</span>
    </label>
  );
}

/** Estado: un punto de color y el texto, sobre un fondo tenue. */
export function Badge({ tone = 'slate', children }: { tone?: 'slate' | 'blue' | 'amber' | 'red'; children: ReactNode }) {
  const styles = {
    slate: 'bg-slate-100 text-slate-700 before:bg-slate-400',
    blue: 'bg-marca-suave text-marca-claro before:bg-marca',
    amber: 'bg-senal-suave text-amber-900 before:bg-senal',
    red: 'bg-red-50 text-red-800 before:bg-red-600',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full ${styles}`}>
      {children}
    </span>
  );
}

export function Alert({ tone = 'error', children }: { tone?: 'error' | 'warning' | 'info' | 'success'; children: ReactNode }) {
  const styles = {
    error: 'border-red-600 bg-red-50 text-red-900',
    warning: 'border-senal bg-senal-suave text-amber-950',
    info: 'border-marca bg-marca-suave text-slate-800',
    success: 'border-green-600 bg-green-50 text-green-950',
  }[tone];
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={`rounded-r border-l-4 px-4 py-2.5 text-sm ${styles}`}>
      {children}
    </div>
  );
}

/**
 * Panel de contenido. `panel` (por defecto) es el contenido principal: blanco y con borde. `aside` es lo secundario
 * (datos, adjuntos, historial), sin borde sobre el fondo, para que la vista vaya primero a lo principal.
 */
export function Card({ title, children, className = '', variant = 'panel' }: { title?: ReactNode; children: ReactNode; className?: string; variant?: 'panel' | 'aside' }) {
  const box = variant === 'aside' ? 'rounded-md bg-slate-100/80 p-4' : 'rounded-md border border-slate-200 bg-white p-5';
  return (
    <section className={`${box} ${className}`}>
      {title && <h2 className={`mb-3 font-semibold text-slate-900 ${variant === 'aside' ? 'text-sm' : 'text-base'}`}>{title}</h2>}
      {children}
    </section>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-marca" aria-hidden />
      {label}
    </div>
  );
}

/** Encabezado de página: título, descripción opcional y las acciones a la derecha. */
export function PageHeader({ title, description, actions, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div className="min-w-0">
        {back}
        <h1 className="text-2xl font-semibold text-tinta">{title}</h1>
        {description && <p className="mt-1 max-w-[65ch] text-sm text-slate-600">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
